import { Fragment, memo, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { RecordContextProvider, useTranslate } from 'ra-core';
import { motion, useReducedMotion } from 'motion/react';
import { EASE_PREMIUM, EASE_PREMIUM_IN, MOTION } from '@/lib/motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Muted } from '@/components/ui/typography';
import { cn, formatDate } from '@/lib/utils';
import {
  WarningIcon,
  RocketLaunchIcon,
  ListMagnifyingGlassIcon,
  CurrencyCircleDollarIcon,
  TagIcon,
  CoinsIcon,
  CalendarIcon,
} from '@phosphor-icons/react';
import { RadioIcon, CheckCircle2Icon } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { Offer } from '../types';

const MotionCard = motion.create(Card);

// Remember which image URLs have already been loaded so remounts skip the pulse.
const loadedSrcs = new Set<string>();

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(value: number, currency = 'USD') {
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency.toUpperCase()}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function OfferStatusBadge({
  tabFilter,
  status,
}: {
  tabFilter: Offer['tab_filter'];
  status: string;
}) {
  const t = useTranslate();

  const icon = (() => {
    switch (tabFilter) {
      case 'pending':
        return <WarningIcon weight="fill" className="text-amber" />;
      case 'accepted':
        return <RadioIcon className="text-emerald" />;
      case 'ended':
        return <CheckCircle2Icon className="text-muted-foreground" />;
    }
  })();

  const badge = (
    <Badge
      variant="outline"
      className="h-6 px-2 py-1 capitalize [&>svg]:size-3.5 bg-secondary border-border rounded-md backdrop-blur-2xl"
    >
      {icon}
      {t(`offer.status.${status}`, { _: status })}
    </Badge>
  );

  if (status === 'counter-in-review') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          The Goloot team is reviewing your counter-offer.
        </TooltipContent>
      </Tooltip>
    );
  }

  return badge;
}

function BrandLogo({ src, name }: { src?: string; name?: string }) {
  // Start loaded if we've seen this URL before (cross-remount memory).
  const [loaded, setLoaded] = useState(() =>
    Boolean(src && loadedSrcs.has(src))
  );
  const imgRef = useRef<HTMLImageElement>(null);

  // useLayoutEffect runs before the browser paints, so already-cached images
  // never show the pulse even for one frame.
  useLayoutEffect(() => {
    if (imgRef.current?.complete) {
      if (src) loadedSrcs.add(src);
      setLoaded(true);
    }
  }, [src]);

  if (!src) {
    return (
      <div className="w-12 h-12 rounded-md border border-border bg-muted shrink-0 flex items-center justify-center text-muted-foreground font-semibold text-sm">
        {name?.charAt(0)}
      </div>
    );
  }

  return (
    <div className="relative w-12 h-12 rounded-md border border-border overflow-hidden shrink-0">
      {!loaded && <div className="absolute inset-0 bg-accent animate-pulse" />}
      <img
        ref={imgRef}
        src={src}
        alt={name}
        className="w-full h-full object-contain"
        onLoad={() => {
          if (src) loadedSrcs.add(src);
          setLoaded(true);
        }}
      />
    </div>
  );
}

const Divider = () => (
  <div className="relative h-0" aria-hidden>
    <div className="absolute inset-x-0 border-t border-dashed border-border" />
  </div>
);

function StatRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center h-6">
      <div className="flex items-center gap-1.5 text-xs font-medium text-foreground [&_svg]:size-4">
        {icon}
        {label}
      </div>
      <div className="ml-auto inline-flex items-center justify-center h-6 px-2 py-1.5 text-xs font-medium text-foreground bg-secondary border border-border rounded-sm font-tabular">
        {value}
      </div>
    </div>
  );
}

function OfferContentSection({ offer }: { offer: Offer }) {
  const t = useTranslate();
  const currency = offer.currency ?? 'USD';

  const items = [
    {
      icon: <CurrencyCircleDollarIcon />,
      label: t('common.text.total_budget'),
      value: formatCurrency(
        offer.last_negotiation?.budget_total ?? 0,
        currency
      ),
    },
    {
      icon: <TagIcon />,
      label: t('common.text.pricing_model'),
      value: t(`offer.pricing_model.${offer.last_negotiation?.pricing_model}`, {
        _: offer.last_negotiation?.pricing_model ?? '—',
      }),
    },
    {
      icon: <CoinsIcon />,
      label: t('common.text.rate'),
      value:
        offer.last_negotiation?.rate > 0
          ? formatCurrency(offer.last_negotiation.rate, currency)
          : 'N/A',
    },
    ...(offer.start_date
      ? [
          {
            icon: <CalendarIcon />,
            label: 'Dates',
            value: offer.end_date
              ? `${formatDate(offer.start_date, 'short')} → ${formatDate(offer.end_date, 'short')}`
              : formatDate(offer.start_date, 'short'),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-2 rounded-md bg-secondary border border-border p-2.5 transition-colors duration-200">
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && <Divider />}
          <StatRow icon={item.icon} label={item.label} value={item.value} />
        </Fragment>
      ))}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────

function OfferCard({ _index = 0, ...offer }: Offer & { _index?: number }) {
  const navigate = useNavigate();
  const { publisherId } = useParams<{ publisherId: string }>();
  const reduced = useReducedMotion();

  const enterDelay = reduced
    ? 0
    : MOTION.stageDelay + Math.min(_index, 7) * MOTION.stagger;

  const displayStatus = offer.tab_filter === 'ended' ? 'ended' : offer.status;
  const needsAction =
    offer.status === 'reviewing' || offer.status === 'new-counter';

  const detailPath = `/publisher/${publisherId}/offers/show/${offer.id}`;

  return (
    <RecordContextProvider value={offer}>
      <MotionCard
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          transition: {
            duration: reduced ? MOTION.reducedEnter : MOTION.enter,
            ease: EASE_PREMIUM,
            delay: enterDelay,
          },
        }}
        exit={{
          opacity: 0,
          scale: 0.96,
          transition: { duration: MOTION.exit, ease: EASE_PREMIUM_IN },
        }}
        transition={{ duration: MOTION.micro, ease: EASE_PREMIUM }}
        onClick={() => navigate(detailPath)}
        className="bg-card backdrop-blur-none px-5 py-5 gap-4 cursor-pointer shadow-premium transition-[box-shadow,border-color] hover:border-border-hover hover:shadow-premium-hover"
      >
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <Muted className="leading-none font-medium truncate min-w-0 flex-1 font-tabular">
            {offer.po_number ?? '—'}
          </Muted>
          <OfferStatusBadge
            tabFilter={offer.tab_filter}
            status={displayStatus}
          />
        </div>

        {/* Brand section */}
        <div className="flex gap-2 items-center">
          <BrandLogo src={offer.brand?.logo} name={offer.brand?.name} />
          <div className="min-w-0 flex-1">
            <Muted className="font-medium truncate">{offer.brand?.name}</Muted>
            <div className="text-lg font-semibold leading-7 truncate">
              {offer.name}
            </div>
          </div>
        </div>

        {/* Content section */}
        <div className="flex-1">
          <OfferContentSection offer={offer} />
        </div>

        {/* CTA */}
        <div onClick={e => e.stopPropagation()}>
          <Button
            asChild
            variant="secondary"
            size="lg"
            className={cn(
              'w-full rounded-md shadow-none !border',
              needsAction &&
                'bg-amber border-amber text-white hover:bg-amber/80 dark:bg-amber/15 dark:border-amber/30 dark:text-amber dark:hover:bg-amber/20 dark:hover:border-amber/40'
            )}
          >
            <Link to={detailPath}>
              {needsAction ? (
                <RocketLaunchIcon weight="fill" className="size-4" />
              ) : (
                <ListMagnifyingGlassIcon className="size-4" />
              )}
              {offer.status === 'new-counter'
                ? 'Review Counter-Offer'
                : needsAction
                  ? 'Review Offer'
                  : 'View Details'}
            </Link>
          </Button>
        </div>
      </MotionCard>
    </RecordContextProvider>
  );
}

export default memo(OfferCard);
